// src/entities/social/model/types.ts
//
// Contract for the merchant social endpoints (`/merchant/social/*`) and for
// the imported-media fields on a product. Shapes are taken from the backend
// handoff (docs/notes/SOCIAL_MEDIA_IMPORT_HANDOFF.md) and cross-checked
// against docs/Api/storemandu-api-docs.json.

/**
 * The path segment on every social route. **Uppercase and case-sensitive** —
 * `instagram` is rejected with a 400, not normalised.
 */
export type SocialPlatform = "INSTAGRAM" | "TIKTOK";

/**
 * TikTok is accepted by the connection routes but its import returns 501, so
 * it stays out of the UI. This is the list the dashboard renders.
 */
export const SUPPORTED_SOCIAL_PLATFORMS: SocialPlatform[] = ["INSTAGRAM"];

/**
 * `null` means never connected (or the row was removed) — the API always
 * returns an entry for every platform, so the key is present either way and
 * the UI never branches on its absence.
 */
export type SocialConnectionStatus =
  | "CONNECTED"
  | "REAUTH_REQUIRED"
  | "DISCONNECTED"
  | null;

export interface SocialConnection {
  platform: SocialPlatform;
  status: SocialConnectionStatus;
  externalUsername: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  connectedAt: string | null;
}

/** Which client starts the flow — decides where the callback sends the browser. */
export type SocialClientType = "WEB" | "IOS" | "ANDROID";

/**
 * Checked against a server-side allowlist by EXACT match. There is no prefix
 * matching, so these two strings are the only ones that work today; anything
 * else is a 400. Both are handled by top-level landing routes that bounce the
 * merchant back into their store-scoped equivalent — see
 * `features/social-import/routes`.
 */
export const SOCIAL_REDIRECT_PATHS = {
  integrations: "/settings/integrations",
  newProduct: "/products/new",
} as const;

export type SocialRedirectPath =
  (typeof SOCIAL_REDIRECT_PATHS)[keyof typeof SOCIAL_REDIRECT_PATHS];

export interface ConnectSocialPayload {
  clientType: SocialClientType;
  redirectPath?: SocialRedirectPath;
}

export interface ConnectSocialResponse {
  /** Navigate the browser here. Single-use and short-lived. */
  authorizeUrl: string;
}

/**
 * Arrives as a query parameter on the landing page, never as the response to
 * the connect call.
 */
export type SocialConnectResult = "connected" | "error";

export type SocialConnectErrorReason =
  | "denied"
  | "wrong_account_type"
  | "account_type_unknown"
  | "scope_missing"
  | "account_already_connected"
  | "state_expired"
  | "state_consumed"
  | "exchange_failed";

// ── Picker ──────────────────────────────────────────────────────────────────

export type SocialMediaType = "IMAGE" | "VIDEO" | "CAROUSEL";

export interface SocialMediaChild {
  externalMediaId: string;
  mediaType: Exclude<SocialMediaType, "CAROUSEL">;
  /**
   * Signed CDN URL that expires within days. For rendering the picker ONLY —
   * never store it, never put it on a product, never send it to import.
   */
  thumbnailUrl: string;
}

export interface SocialMediaItem {
  externalMediaId: string;
  mediaType: SocialMediaType;
  /** See the warning on {@link SocialMediaChild.thumbnailUrl}. */
  thumbnailUrl: string;
  permalink: string;
  caption: string | null;
  createdAt: string;
  /** Present on a CAROUSEL. The album itself is not importable. */
  children?: SocialMediaChild[];
}

export interface SocialMediaPage {
  items: SocialMediaItem[];
  /** `null` means this was the last page. */
  nextCursor: string | null;
}

// ── Import ──────────────────────────────────────────────────────────────────

export interface ImportSocialMediaItem {
  externalMediaId: string;
  /**
   * The album id, required when this item is one of a carousel's `children`.
   * A child has no permalink or caption of its own; omitting this comes back
   * as `not_found`, which is the single most common cause of a partly-failed
   * batch.
   */
  parentExternalMediaId?: string;
}

export interface ImportSocialMediaPayload {
  items: ImportSocialMediaItem[];
  /** Absent while the product is still being created and has no id yet. */
  productId?: string;
}

/**
 * What import hands back, and what the product write takes as `importedMedia`.
 * Pass these through UNCHANGED — the link back to the post is what drives the
 * platform badge and the deletion path on disconnect.
 */
export interface ImportedMediaDescriptor {
  /**
   * An image: a StoreMandu blob, renders in an `<img>`.
   * A video: the Instagram post address, meant to be EMBEDDED. Feeding it to
   * `<img>` or `<video src>` gives a broken element.
   */
  url: string;
  /** `"video"` means embed; `null`/absent means image. */
  mimeType: "video" | null;
  source: SocialPlatform;
  externalMediaId: string;
  permalink: string;
}

export type ImportFailureReason =
  /** Not an error — already on this product. Show "already added". */
  | "already_imported"
  /** Deleted, a carousel parent, or a child sent without its parent. */
  | "not_found"
  | "not_public"
  | "unsupported_type"
  | "too_large"
  | "empty_file"
  | "size_unknown"
  /** Retryable. */
  | "copy_failed"
  /** Retryable. */
  | "upstream_unavailable";

export interface ImportFailure {
  externalMediaId: string;
  reason: ImportFailureReason;
}

export interface ImportMediaResponse {
  media: ImportedMediaDescriptor[];
  /** Everything that did not land in `media`. Never treat a non-empty
   *  `failed` as a failed request — the batch is not all-or-nothing. */
  failed: ImportFailure[];
  /** The first selected post's caption, offered for prefilling. */
  caption: string | null;
  /** That post's hashtags, offered for prefilling. */
  tags: string[];
}

/** The two reasons worth offering a retry for. */
export const RETRYABLE_IMPORT_REASONS: ImportFailureReason[] = [
  "copy_failed",
  "upstream_unavailable",
];

export function isRetryableImportFailure(reason: ImportFailureReason): boolean {
  return RETRYABLE_IMPORT_REASONS.includes(reason);
}

// ── Disconnect ──────────────────────────────────────────────────────────────

export interface DisconnectPreviewProduct {
  id: string;
  title: string;
  images: number;
  videos: number;
}

export interface DisconnectPreview {
  connected: boolean;
  platform: SocialPlatform;
  connectedAs: string | null;
  connectedAt: string | null;
  importedMedia: { images: number; videos: number; total: number };
  /** Always the TRUE count, even when `products` is truncated. */
  productsAffected: number;
  /** Capped at 50. */
  products: DisconnectPreviewProduct[];
  productsTruncated: boolean;
  /**
   * Policy, returned rather than hardcoded so a stale client stops warning
   * once the rule changes. Branch on it.
   */
  deletesImportedMedia: boolean;
}

export interface DisconnectResponse {
  /** `false` means there was nothing to disconnect. Not an error. */
  disconnected: boolean;
  /** Safe to render as-is; says which of the two happened. */
  message: string;
  mediaDeleted: number;
  productsAffected: number;
}

// ── Media as it reads back off a product ────────────────────────────────────

/** Where a product media item came from. Drives the platform badge. */
export type ProductMediaSource = "UPLOAD" | SocialPlatform;

/** The field to branch on when rendering. */
export type ProductMediaKind = "image" | "video" | "embed";

export interface ProductMediaItem {
  url: string;
  mimeType: "video" | null;
  source: ProductMediaSource;
  externalMediaId: string | null;
  permalink: string | null;
  kind: ProductMediaKind;
}

// ── Limits ──────────────────────────────────────────────────────────────────

/**
 * What the server actually enforces.
 *
 * The Swagger description on the import endpoint still says a product holds
 * "at most 10 images and 2 videos" — that limit was specified and never
 * implemented. These are the real numbers; expect the tighter ones to arrive
 * later as a breaking change.
 */
export const SOCIAL_IMPORT_LIMITS = {
  /** Items per import request. 400 on import beyond this. */
  itemsPerRequest: 10,
  /** Total image bytes per request — rejected before anything is copied. */
  bytesPerRequest: 40 * 1024 * 1024,
  /** Per image; that item comes back `too_large`. */
  bytesPerImage: 10 * 1024 * 1024,
  /** `importedMedia` entries per product. 400 on the product write. */
  perProduct: 12,
} as const;

/** Page size for the picker. The endpoint caps at 50. */
export const SOCIAL_MEDIA_PAGE_SIZE = 20;
